import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { useTitel } from "../titel";

/**
 * Passwort selbst setzen, mit einem Einladungslink und ohne Anmeldung.
 *
 * Diese Seite liegt ausserhalb des Rahmens (siehe main.tsx): wer hier
 * landet, hat noch kein Passwort — oder hat es vergessen. Derselbe Weg
 * dient in beiden Fällen.
 */
export default function Einladung() {
  useTitel("Einladung");
  const { token = "" } = useParams();
  const [passwort, setPasswort] = useState("");
  const [wiederholung, setWiederholung] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fertig, setFertig] = useState(false);

  const { data, isPending, error } = useQuery({
    queryKey: ["einladung", token],
    queryFn: () => api.einladungPruefen(token),
    retry: false,
  });

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    // Vor dem Netzwerk prüfen, was man ohne Netzwerk prüfen kann.
    if (passwort !== wiederholung) {
      setFehler("Die beiden Passwörter sind nicht gleich.");
      return;
    }
    if (passwort.length < 10) {
      setFehler("Mindestens 10 Zeichen.");
      return;
    }
    setLaeuft(true);
    try {
      await api.einladungEinloesen(token, passwort);
      setFertig(true);
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Das hat nicht geklappt.");
    } finally {
      setLaeuft(false);
    }
  }

  const feld =
    "mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 outline-none " +
    "focus:border-black/40 dark:border-white/15 dark:bg-neutral-800";

  if (isPending) {
    return <p className="mx-auto mt-24 max-w-sm px-4 text-black/40">lädt …</p>;
  }

  if (error) {
    return (
      <div className="mx-auto mt-24 max-w-sm px-4">
        <h1 className="text-2xl font-semibold tracking-tight">Stammbaum</h1>
        <p className="mt-4 text-red-700">
          {error instanceof Error ? error.message : "Dieser Link ist ungültig oder abgelaufen."}
        </p>
        <p className="mt-2 text-sm text-black/50 dark:text-white/50">
          Einladungslinks gelten sieben Tage und nur ein einziges Mal. Bitte um einen neuen
          fragen.
        </p>
      </div>
    );
  }

  if (fertig) {
    return (
      <div className="mx-auto mt-24 max-w-sm px-4">
        <h1 className="text-2xl font-semibold tracking-tight">Passwort gesetzt</h1>
        <p className="mt-2 text-sm text-black/60 dark:text-white/60">
          Du kannst dich jetzt mit <strong>{data.email}</strong> anmelden.
        </p>
        <Link
          to="/anmelden"
          className="mt-6 inline-block rounded-lg bg-black px-4 py-2 text-sm text-white dark:bg-white dark:text-black"
        >
          Zur Anmeldung
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-24 max-w-sm px-4">
      <h1 className="text-2xl font-semibold tracking-tight">
        {data.erstmalig ? "Willkommen" : "Neues Passwort"}
      </h1>
      <p className="mt-1 text-sm text-black/50 dark:text-white/50">
        {data.anzeigename} · {data.email}
      </p>
      <p className="mt-4 text-sm text-black/60 dark:text-white/60">
        {data.erstmalig
          ? "Bitte setz dir ein Passwort. Mindestens 10 Zeichen; ein Satz, den du dir merkst, ist besser als ein kurzes Wort mit Sonderzeichen."
          : "Setz dir ein neues Passwort. Alle offenen Sitzungen werden dabei abgemeldet."}
      </p>

      <form onSubmit={absenden} className="mt-6 space-y-4">
        <label className="block">
          <span className="text-sm text-black/60 dark:text-white/60">Passwort</span>
          <input
            type="password"
            autoComplete="new-password"
            autoFocus
            value={passwort}
            onChange={(e) => setPasswort(e.target.value)}
            className={feld}
          />
        </label>
        <label className="block">
          <span className="text-sm text-black/60 dark:text-white/60">noch einmal</span>
          <input
            type="password"
            autoComplete="new-password"
            value={wiederholung}
            onChange={(e) => setWiederholung(e.target.value)}
            className={feld}
          />
        </label>

        {fehler && <p className="text-sm text-red-700">{fehler}</p>}

        <button
          type="submit"
          disabled={laeuft}
          className="w-full rounded-lg bg-black px-4 py-2 text-white disabled:opacity-50 dark:bg-white dark:text-black"
        >
          {laeuft ? "einen Moment …" : "Passwort setzen"}
        </button>
      </form>
    </div>
  );
}
